import { useRef, useEffect } from 'react';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function PrinciplesSection() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.4;
    }
  }, []);

  return (
    <section id="vision-mission" className="relative py-28 md:py-40 lg:py-48 px-6 md:px-10 lg:px-16 bg-background-50 overflow-hidden scroll-mt-20">
      {/* Background video */}
      <div className="absolute inset-0 opacity-[0.18]">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(1.1) contrast(1.05)' }}
        >
          <source src="https://public.readdy.ai/ai/video_res/019f64b0-22d1-7671-9f38-0c2d6c57b405.mp4" type="video/mp4" />
        </video>
      </div>
      {/* Light overlay */}
      <div className="absolute inset-0 bg-background-50/30 pointer-events-none"></div>

      {/* Subtle gradient accent at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent"></div>

      {/* Single subtle ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-accent-500/[0.08] blur-[150px] pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-0">
          {/* What Drives Us */}
          <ScrollReveal duration={450}>
            <div className="lg:pr-16 lg:border-r border-background-300/40 flex flex-col h-full">
              <div>
                <span className="inline-block text-xs md:text-sm font-medium tracking-[0.25em] uppercase text-accent-600 mb-5 md:mb-6">
                  What Drives Us
                </span>
                <p className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-primary-800 leading-[1.15] tracking-tight mb-8">
                  To make every decision a{' '}
                  <span className="text-accent-500">data-driven</span> one.
                </p>
              </div>
              <div className="mt-auto">
                <div className="relative rounded-2xl overflow-hidden aspect-[16/9] bg-background-200 group cursor-pointer">
                  <div className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/10 transition-colors duration-500 z-10 pointer-events-none"></div>
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20business%20meeting%20room%20with%20professionals%20gathered%20around%20a%20large%20screen%20displaying%20data%20analytics%20dashboards%2C%20warm%20neutral%20gray%20interior%20palette%20with%20subtle%20teal%20accent%20details%20on%20the%20screen%20charts%2C%20natural%20daylight%20through%20windows%20creating%20soft%20diffused%20lighting%2C%20minimalist%20office%20design%20with%20clean%20lines%20and%20muted%20tones%2C%20professional%20collaborative%20atmosphere%2C%20candid%20documentary%20style%20photography%2C%20color%20palette%20dominated%20by%20warm%20grays%20and%20charcoal%20with%20hints%20of%20muted%20teal%20green%2C%20sophisticated%20corporate%20environment%2C%20editorial%20quality%20with%20soft%20natural%20contrast&width=800&height=450&seq=principles-drives-us-v3&orientation=landscape&nocache=true"
                    alt="Data-driven decision making visualization"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Where We're Headed */}
          <ScrollReveal delay={100} duration={450}>
            <div className="lg:pl-16 flex flex-col h-full">
              <div>
                <span className="inline-block text-xs md:text-sm font-medium tracking-[0.25em] uppercase text-accent-600 mb-5 md:mb-6">
                  Where We&apos;re Headed
                </span>
                <p className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-primary-800 leading-[1.15] tracking-tight mb-8">
                  A world where data{' '}
                  <span className="text-accent-500">leads the way.</span>
                </p>
              </div>
              <div className="mt-auto">
                <div className="relative rounded-2xl overflow-hidden aspect-[16/9] bg-background-200 group cursor-pointer">
                  <div className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/10 transition-colors duration-500 z-10 pointer-events-none"></div>
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20glass%20walled%20office%20interior%20overlooking%20city%20skyline%20at%20early%20morning%2C%20warm%20gray%20and%20soft%20charcoal%20tones%20dominating%20the%20architecture%2C%20subtle%20muted%20teal%20green%20accents%20in%20glass%20reflections%20and%20interior%20details%2C%20natural%20diffused%20daylight%20creating%20gentle%20atmosphere%2C%20sleek%20minimalist%20workspace%2C%20the%20contrast%20between%20dim%20interior%20and%20softly%20brightening%20exterior%20suggesting%20emerging%20clarity%2C%20sophisticated%20corporate%20atmosphere%2C%20cinematic%20composition%20with%20balanced%20neutral%20color%20palette%2C%20hopeful%20forward%20looking%20mood%2C%20premium%20architectural%20photography%20style%20with%20restrained%20earthy%20tones&width=800&height=450&seq=principles-headed-v3&orientation=landscape&nocache=true"
                    alt="Future vision of data-driven world"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}